import React, { useMemo } from 'react';

/**
 * Static entries that never change — defined at module level so they're
 * never recreated between renders.
 */
const STATIC_DISPLAY = {
  '{alt}':          '123',
  '{smileys}':      '\uD83D\uDE03',
  '{shift}':        '⇧',
  '{tab}':          'Tab',
  '{shiftactivated}': '⇧',
  '{enter}':        'return',
  '{bksp}':         '⌫',
  '{altright}':     '.?123',
  '{downkeyboard}': '🞃',
  '{space}':        'space',
  '{default}':      'ABC',
  '{back}':         '⇦',
  '{lock}':         'Caps',
  '{cancel}':       'Cancel',
  '{done}':         'Done',
  '{clear}':        'CLR',
  '{surveyBack}':   '⇦',
  '{emptySpace}':   React.createElement(React.Fragment, null, '\u00A0'),
  '{continue}':     'Continue',
  '{special}':      '=\\<',
  '{lowercase}':    'abc',
};

/**
 * Build the keyboard display map.
 * Splits into a static constant (never recreated) and a small dynamic portion
 * that only rebuilds when theme color, screenType, type, or deviceInfo changes —
 * NOT on every keystroke.
 *
 * @param {string} primaryText   — serverAppTheme.primary_text (for SVG stroke color)
 * @param {string} screenType
 * @param {string} type
 * @param {object} deviceInfo    — oauth.deviceInformation
 */
export function useKeyDisplay(primaryText, screenType, type, deviceInfo) {
  const dynamicDisplay = useMemo(() => {
    const returnSvg = (
      <svg width="50" height="25" viewBox="0 0 50 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="1.86599" y1="15.3249" x2="10.8874" y2="7.80784" stroke={primaryText} strokeWidth="3" strokeLinecap="round" />
        <line x1="2.11079" y1="15.7889" x2="10.7889" y2="22.8892" stroke={primaryText} strokeWidth="3" strokeLinecap="round" />
        <path d="M33.9764 2V6.70502C33.9764 12.2975 30.5329 16.0214 24.9405 15.9878C17.8471 15.945 8.17867 15.8052 2 15.8052" stroke={primaryText} strokeWidth="3" strokeLinecap="round" />
      </svg>
    );

    const binSvg = (
      <div className={screenType !== 'cashCalculator' ? 'h-100' : ''}>
        <svg
          width="22"
          height={type === 'textWithoutNumeric' ? '47' : '42'}
          viewBox="0 0 22 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.01641 25.8489C3.28005 25.8489 2.64969 25.5705 2.12531 25.0138C1.60093 24.4571 1.33874 23.7879 1.33874 23.0062V4.52868H-9.15527e-05V1.68599H6.69408V0.264648H14.7271V1.68599H21.4213V4.52868H20.0824V23.0062C20.0824 23.7879 19.8202 24.4571 19.2959 25.0138C18.7715 25.5705 18.1411 25.8489 17.4048 25.8489H4.01641ZM17.4048 4.52868H4.01641V23.0062H17.4048V4.52868ZM6.69408 20.1635H9.37176V7.37138H6.69408V20.1635ZM12.0494 20.1635H14.7271V7.37138H12.0494V20.1635Z"
            fill={primaryText}
          />
        </svg>
      </div>
    );

    const swipeDisplay =
      type === 'swipe' &&
      deviceInfo?.deviceGroup?.serverAppSettings?.enableLoginWithCard
        ? '💳'
        : React.createElement(React.Fragment, null, '\u00A0');

    return {
      '{return}':  returnSvg,
      '{bin}':     binSvg,
      '{swipe}':   swipeDisplay,
    };
  }, [primaryText, screenType, type, deviceInfo]);

  // Merge: static first, dynamic overrides (dynamic is a tiny object so spread is fine)
  return useMemo(
    () => ({ ...STATIC_DISPLAY, ...dynamicDisplay }),
    [dynamicDisplay],
  );
}
